import React, { useState, useCallback, useRef, useEffect } from 'react';
import presentationService from '../services/PresentationService';
import { FileWithContent } from '../services/PresentationService';
import claudeService, { CLAUDE_MAX_TOKENS } from '../services/ClaudeService';
import { debounce } from 'lodash';

interface FileUploadContextProps {
  onFilesProcessed: (files: FileWithContent[]) => void;
  slideTitleContent?: string;
  slideBodyContent?: string;
}

const FileUploadContext: React.FC<FileUploadContextProps> = ({ 
  onFilesProcessed, 
  slideTitleContent = '',
  slideBodyContent = ''
}) => {
  const [files, setFiles] = useState<FileWithContent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [tokenCount, setTokenCount] = useState<number>(0);
  const [tokenLimit] = useState<number>(CLAUDE_MAX_TOKENS);
  const [tokenUpdateLoading, setTokenUpdateLoading] = useState<boolean>(false);
  
  // Create a debounced version of the token count function
  const debouncedTokenCount = useRef(
    debounce(async (contentText: string, currentFiles: FileWithContent[]) => {
      setTokenUpdateLoading(true);
      try {
        const count = await claudeService.countTokens(contentText, currentFiles);
        setTokenCount(count);
      } catch (error) {
        console.error('Error counting tokens:', error);
      } finally {
        setTokenUpdateLoading(false);
      }
    }, 1000)
  ).current;
  
  // Update token count whenever files, title or content changes
  useEffect(() => {
    const contentText = `${slideTitleContent}\n${slideBodyContent}`;
    debouncedTokenCount(contentText, files);
    
    // Cleanup debounce on unmount
    return () => {
      debouncedTokenCount.cancel();
    };
  }, [files, slideTitleContent, slideBodyContent, debouncedTokenCount]);
  
  const processFiles = useCallback(async (selectedFiles: FileList) => {
    setIsProcessing(true);
    setError(null);
    
    try {
      const processedFiles: FileWithContent[] = [];
      const allProcessingFiles: FileWithContent[] = [...files]; // Keep track of all files including in-process ones
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        // Skip existing files with the same name
        if (files.some(f => f.name === file.name)) {
          continue;
        }
        
        // Set initial progress for this file
        const fileId = `${file.name}-${Date.now()}`;
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        
        // Allow only PDF and text files
        if (!file.type.match('application/pdf') && 
            !file.type.match('text/') && 
            !file.name.endsWith('.md') &&
            !file.type.match('application/json')) {
          setError(`Unsupported file type: ${file.type}. Only PDF and text files are supported.`);
          continue;
        }
        
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          setError(`File size exceeds 10MB limit: ${file.name}`);
          continue;
        }
        
        try {
          // Update progress to show upload starting
          setUploadProgress(prev => ({ ...prev, [fileId]: 10 }));
          
          // Upload file to Firebase Storage 
          const uploadedFile = await presentationService.uploadContextFile(file);
          
          // Update progress after successful upload
          setUploadProgress(prev => ({ ...prev, [fileId]: 50 }));
          
          // Add file to temporary collection for token counting
          allProcessingFiles.push(uploadedFile);
          
          // Calculate token count for this batch so far (avoiding duplicate counts)
          const contentText = `${slideTitleContent}\n${slideBodyContent}`;
          
          // Only check token count if we haven't recently checked
          if (i === 0 || i % 3 === 0 || i === selectedFiles.length - 1) {
            const currentTotalTokens = await claudeService.countTokens(contentText, allProcessingFiles);
            
            // Check if adding this file would exceed token limit
            if (currentTotalTokens > tokenLimit) {
              setError(`Adding "${file.name}" would exceed Claude's token limit. Please remove other files first.`);
              setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
              // Still continue processing other files
              continue;
            }
          }
          
          // Store token count in the file object - we'll calculate this once at the end
          processedFiles.push(uploadedFile);
          setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        } catch (err) {
          console.error(`Error processing file ${file.name}:`, err);
          setError(`Error processing file ${file.name}`);
          setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        }
      }
      
      // Update the files state with new files that don't exceed token limit
      const updatedFiles = [...files, ...processedFiles];
      
      // Calculate final token counts if we have processed files
      if (processedFiles.length > 0) {
        const contentText = `${slideTitleContent}\n${slideBodyContent}`;
        
        // Get total token count for all files
        const totalTokens = await claudeService.countTokens(contentText, updatedFiles);
        setTokenCount(totalTokens);
        
        // No need to set individual file token counts since we're tracking total usage
      }
      
      setFiles(updatedFiles);
      onFilesProcessed(updatedFiles);
      setIsProcessing(false);
      
      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress({});
      }, 1000);
    } catch (err) {
      console.error('Error processing files:', err);
      setError('Error processing files. Please try again.');
      setIsProcessing(false);
    }
  }, [files, onFilesProcessed, slideTitleContent, slideBodyContent, tokenLimit]);
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      processFiles(event.target.files);
    }
  };
  
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      processFiles(event.dataTransfer.files);
    }
  }, [processFiles]);
  
  const handleRemoveFile = (id: string) => {
    const updatedFiles = files.filter(file => file.id !== id);
    setFiles(updatedFiles);
    onFilesProcessed(updatedFiles);
  };
  
  const handleClearAll = () => {
    setFiles([]);
    onFilesProcessed([]);
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const getFileIcon = (file: FileWithContent) => {
    if (file.type.match('application/pdf')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-900/30 border border-red-500/30 text-red-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
            <path d="M7 10H11V8H7V10Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
          </svg>
        </div>
      );
    } else if (file.type.match('text/')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-900/30 border border-blue-500/30 text-blue-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3 5C3 3.34315 4.34315 2 6 2H14C14.2652 2 14.5196 2.10536 14.7071 2.29289L20.7071 8.29289C20.8946 8.48043 21 8.73478 21 9V19C21 20.6569 19.6569 22 18 22H6C4.34315 22 3 20.6569 3 19V5ZM13 4H6C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10H14C13.4477 10 13 9.55228 13 9V4ZM15 4.41421V8H18.5858L15 4.41421Z" fill="currentColor" />
            <path d="M7 14C7 13.4477 7.44772 13 8 13H16C16.5523 13 17 13.4477 17 14C17 14.5523 16.5523 15 16 15H8C7.44772 15 7 14.5523 7 14Z" fill="currentColor" />
            <path d="M7 18C7 17.4477 7.44772 17 8 17H16C16.5523 17 17 17.4477 17 18C17 18.5523 16.5523 19 16 19H8C7.44772 19 7 18.5523 7 18Z" fill="currentColor" />
            <path d="M7 10C7 9.44772 7.44772 9 8 9H10C10.5523 9 11 9.44772 11 10C11 10.5523 10.5523 11 10 11H8C7.44772 11 7 10.5523 7 10Z" fill="currentColor" />
          </svg>
        </div>
      );
    } else if (file.name.endsWith('.md')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-900/30 border border-purple-500/30 text-purple-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3 5C3 3.34315 4.34315 2 6 2H18C19.6569 2 21 3.34315 21 5V19C21 20.6569 19.6569 22 18 22H6C4.34315 22 3 20.6569 3 19V5ZM6 4C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V5C19 4.44772 18.5523 4 18 4H6Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M7 7C7 6.44772 7.44772 6 8 6H16C16.5523 6 17 6.44772 17 7C17 7.55228 16.5523 8 16 8H8C7.44772 8 7 7.55228 7 7Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M7 11C7 10.4477 7.44772 10 8 10H16C16.5523 10 17 10.4477 17 11C17 11.5523 16.5523 12 16 12H8C7.44772 12 7 11.5523 7 11Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M7 15C7 14.4477 7.44772 14 8 14H16C16.5523 14 17 14.4477 17 15C17 15.5523 16.5523 16 16 16H8C7.44772 16 7 15.5523 7 15Z" fill="currentColor" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 text-gray-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M14 2.75C13.5858 2.75 13.25 3.08579 13.25 3.5V8.5C13.25 9.74264 14.2574 10.75 15.5 10.75H20.5C20.9142 10.75 21.25 10.4142 21.25 10C21.25 9.58579 20.9142 9.25 20.5 9.25H15.5C15.0858 9.25 14.75 8.91421 14.75 8.5V3.5C14.75 3.08579 14.4142 2.75 14 2.75ZM7.75 6.5C7.75 6.08579 7.41421 5.75 7 5.75C6.58579 5.75 6.25 6.08579 6.25 6.5V17.5C6.25 17.9142 6.58579 18.25 7 18.25C7.41421 18.25 7.75 17.9142 7.75 17.5V6.5ZM10.75 6.5C10.75 6.08579 10.4142 5.75 10 5.75C9.58579 5.75 9.25 6.08579 9.25 6.5V17.5C9.25 17.9142 9.58579 18.25 10 18.25C10.4142 18.25 10.75 17.9142 10.75 17.5V6.5Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M10.25 2H5C3.34315 2 2 3.34315 2 5V19C2 20.6569 3.34315 22 5 22H19C20.6569 22 22 20.6569 22 19V10.25C22 10.25 22 10.25 22 10.25H21.75H15.5C13.9812 10.25 12.75 9.01878 12.75 7.5V1.25V1C12.75 1 12.75 1 12.75 1H12.5H10.25ZM3.5 5C3.5 4.17157 4.17157 3.5 5 3.5H11.25V7.5C11.25 9.84721 13.1528 11.75 15.5 11.75H20.5V19C20.5 19.8284 19.8284 20.5 19 20.5H5C4.17157 20.5 3.5 19.8284 3.5 19V5Z" fill="currentColor" />
          </svg>
        </div>
      );
    }
  };
  
  // Add token usage UI display
  const renderTokenUsage = () => {
    const percentage = (tokenCount / tokenLimit) * 100;
    const isNearLimit = percentage > 80;
    const isAtLimit = percentage >= 100;
    
    return (
      <div className="mt-4 mb-2">
        <div className="flex justify-between text-xs mb-1">
          <span>Token Usage</span>
          <span className={`${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-gray-400'}`}>
            {tokenUpdateLoading ? 'Calculating...' : `${tokenCount.toLocaleString()} / ${tokenLimit.toLocaleString()}`}
          </span>
        </div>
        <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
          <div 
            className={`h-full ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-blue-600'}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        {isAtLimit && (
          <p className="text-red-400 text-xs mt-1">
            Context window full. Please remove some files to add more.
          </p>
        )}
        {isNearLimit && !isAtLimit && (
          <p className="text-yellow-400 text-xs mt-1">
            Approaching token limit. Limited space for more context.
          </p>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {error && (
        <div className="relative mt-4 p-4 rounded-lg border border-red-600/20 bg-red-950/20 text-red-200 mb-4 backdrop-blur-sm animate-fade-in">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="inline-flex bg-red-950/30 rounded-md p-1.5 text-red-400 hover:text-red-300 focus:outline-none"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Display token usage */}
      {renderTokenUsage()}

      <div
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-300 group overflow-hidden ${
          isDragging 
            ? 'border-teal-500 bg-teal-500/10' 
            : 'border-white/10 hover:border-teal-500/50 bg-gradient-to-b from-white/5 to-transparent'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-transparent to-teal-600/5 pointer-events-none"></div>
        
        <input
          type="file"
          className="hidden"
          onChange={handleFileChange}
          multiple
          ref={fileInputRef}
          accept=".pdf,.txt,.md,.json"
        />
        
        <div className="text-center pointer-events-none">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className={`p-4 rounded-full border backdrop-blur-sm transition-all duration-300 ${isDragging ? 'border-teal-500 bg-teal-500/10 scale-110' : 'border-white/10 bg-white/5'}`}>
              <svg className={`h-8 w-8 transition-all duration-300 ${isDragging ? 'text-teal-400' : 'text-white/70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            
            <div className="space-y-1">
              <p className={`text-lg font-medium transition-colors duration-300 ${isDragging ? 'text-teal-300' : 'text-white/80'}`}>
                {isDragging ? 'Drop files to upload' : 'Drag and drop files here'}
              </p>
              <p className="text-sm text-gray-400">or click to select files</p>
              <p className="text-xs text-gray-500 mt-2">PDF, TXT, MD, JSON (Max 10MB)</p>
            </div>
          </div>
        </div>

        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="text-xs px-2 py-1 rounded-md bg-white/10 text-gray-300 backdrop-blur-sm">
            Click or drop
          </div>
        </div>
      </div>
      
      {isProcessing && (
        <div className="animate-pulse">
          <div className="flex items-center space-x-2 p-4 border border-white/10 rounded-lg bg-white/5 backdrop-blur-sm">
            <svg className="animate-spin h-5 w-5 text-teal-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div>
              <div className="text-sm font-medium text-white">Processing files...</div>
              <div className="text-xs text-gray-400">This may take a moment</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="relative h-1 w-full bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="absolute h-full bg-gradient-to-r from-teal-500 to-violet-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          ))}
        </div>
      )}
      
      {files.length > 0 && (
        <div className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center">
              <svg className="w-5 h-5 mr-2 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span>{files.length} {files.length === 1 ? 'File' : 'Files'} Added</span>
            </h3>
            <button
              onClick={handleClearAll}
              className="text-sm text-red-400 hover:text-red-300 transition-colors duration-200 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              Clear All
            </button>
          </div>
          
          <div className="space-y-3 glassmorphism rounded-xl overflow-hidden border border-white/10">
            {files.map((file) => (
              <div
                key={file.id}
                className="group p-3 transition-all duration-300 hover:bg-white/5 border-b border-white/5 last:border-b-0"
              >
                <div className="flex items-start">
                  {getFileIcon(file)}
                  
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-white truncate group-hover:text-teal-300 transition-colors duration-200">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                          {file.summary || formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(file.id);
                        }}
                        className="ml-2 text-gray-400 hover:text-red-400 p-1 rounded-full hover:bg-red-900/20 transition-all duration-200"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                    
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <span className="flex items-center">
                        <span className="inline-block w-2 h-2 rounded-full mr-1.5" 
                          style={{
                            backgroundColor: 
                              file.type.match('application/pdf') ? '#ef4444' : 
                              file.type.match('text/') ? '#3b82f6' : 
                              file.name.endsWith('.md') ? '#8b5cf6' : 
                              '#6b7280'
                          }}
                        ></span>
                        {file.type.match('application/pdf') ? 'PDF' : 
                         file.type.match('text/') ? 'Text' : 
                         file.name.endsWith('.md') ? 'Markdown' :
                         file.type.split('/')[1]?.toUpperCase() || 'File'}
                      </span>
                      <span className="mx-2">•</span>
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default FileUploadContext; 